import { useMemo } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
  Combobox,
  type ComboboxOption,
  cn,
} from "@tellescope/ui";
import { US_STATES } from "@tellescope/utils";
import { useTranslation } from "react-i18next";
import { CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import type { ProfileEditBlock as ProfileEditBlockType } from "../../../shared/schema";
import { useProfileEdit, type ResolvedFieldConfig } from "../../hooks/useProfileEdit";

interface ProfileEditBlockProps {
  block: ProfileEditBlockType;
}

interface ProfileFieldInputProps {
  field: ResolvedFieldConfig;
  onChange: (value: string) => void;
}

function ProfileFieldInput({ field, onChange }: ProfileFieldInputProps) {
  // Determine input type based on field key
  const getInputType = () => {
    if (field.key === "email") return "email";
    if (field.key === "phone") return "tel";
    if (field.key === "zipCode") return "text";
    return "text";
  };

  // Determine autocomplete attribute for better mobile UX
  const getAutocomplete = () => {
    switch (field.key) {
      case "fname":
        return "given-name";
      case "lname":
        return "family-name";
      case "email":
        return "email";
      case "phone":
        return "tel";
      case "addressLineOne":
        return "address-line1";
      case "addressLineTwo":
        return "address-line2";
      case "city":
        return "address-level2";
      case "state":
        return "address-level1";
      case "zipCode":
        return "postal-code";
      default:
        return undefined;
    }
  };

  return (
    <div className="space-y-2">
      <Label htmlFor={field.key} className="text-sm font-medium">
        {field.label}
        {field.required && <span className="text-destructive ml-1">*</span>}
      </Label>
      <Input
        id={field.key}
        type={getInputType()}
        autoComplete={getAutocomplete()}
        value={field.value}
        onChange={(e) => onChange(e.target.value)}
        disabled={!field.editable}
        className={cn(!field.editable && "bg-muted cursor-not-allowed")}
        aria-required={field.required}
      />
    </div>
  );
}

interface StateSelectFieldProps {
  field: ResolvedFieldConfig;
  onChange: (value: string) => void;
}

function StateSelectField({ field, onChange }: StateSelectFieldProps) {
  const { t } = useTranslation("contacts");

  // Convert US_STATES to ComboboxOption format
  const stateOptions: ComboboxOption[] = useMemo(
    () =>
      US_STATES.map((state) => ({
        value: state.value,
        label: state.label,
      })),
    [t]
  );

  // Find the selected option based on the value
  const selectedOption = useMemo(
    () => stateOptions.find((option) => option.value === field.value),
    [stateOptions, field.value]
  );

  // Handle change by extracting the value from the ComboboxOption
  const handleChange = (option: ComboboxOption | undefined) => {
    onChange(option?.value ?? "");
  };

  return (
    <div className="space-y-2">
      <Label htmlFor={field.key} className="text-sm font-medium">
        {field.label}
        {field.required && <span className="text-destructive ml-1">*</span>}
      </Label>
      <Combobox
        value={selectedOption}
        onChange={handleChange}
        options={stateOptions}
        placeholder={t("Select a state")}
        searchPlaceholder={t("Search states...")}
        className={cn(!field.editable && "opacity-50 pointer-events-none")}
        aria-label={field.label}
        customFilter={(searchTerm, options) => {
          return options.filter((option) =>
            `${option.label.toLowerCase()}|${option.value.toLowerCase()}`.includes(searchTerm.toLowerCase())
          );
        }}
      />
    </div>
  );
}

export function ProfileEditBlock({ block }: ProfileEditBlockProps) {
  const { t } = useTranslation("contacts");
  const { fields, isDirty, setValue, resetForm, submitForm, isSubmitting, error, successMessage } = useProfileEdit({
    block,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitForm();
  };

  // Show fields in a grid layout for address fields
  const personalFields = fields.filter((f) => ["fname", "lname", "email", "phone"].includes(f.key));
  const addressFields = fields.filter((f) =>
    ["addressLineOne", "addressLineTwo", "city", "state", "zipCode"].includes(f.key)
  );

  return (
    <Card>
      {block.props.title && (
        <CardHeader className="pb-4">
          <CardTitle className="text-lg">{block.props.title}</CardTitle>
        </CardHeader>
      )}
      <CardContent className={cn(!block.props.title && "pt-6")}>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal information */}
          {personalFields.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {personalFields.map((field) => (
                <ProfileFieldInput key={field.key} field={field} onChange={(value) => setValue(field.key, value)} />
              ))}
            </div>
          )}

          {/* Address information */}
          {addressFields.length > 0 && (
            <div className="space-y-4">
              {/* Address lines - full width */}
              {addressFields
                .filter((f) => ["addressLineOne", "addressLineTwo"].includes(f.key))
                .map((field) => (
                  <ProfileFieldInput key={field.key} field={field} onChange={(value) => setValue(field.key, value)} />
                ))}

              {/* City, State, ZIP - responsive grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {addressFields
                  .filter((f) => f.key === "city")
                  .map((field) => (
                    <div key={field.key} className="col-span-2 sm:col-span-2">
                      <ProfileFieldInput field={field} onChange={(value) => setValue(field.key, value)} />
                    </div>
                  ))}
                {addressFields
                  .filter((f) => f.key === "state")
                  .map((field) => (
                    <div key={field.key} className="col-span-1">
                      <StateSelectField field={field} onChange={(value) => setValue(field.key, value)} />
                    </div>
                  ))}
                {addressFields
                  .filter((f) => f.key === "zipCode")
                  .map((field) => (
                    <div key={field.key} className="col-span-1">
                      <ProfileFieldInput field={field} onChange={(value) => setValue(field.key, value)} />
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Status messages */}
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {successMessage && !error && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-green-50 text-green-700 text-sm dark:bg-green-950 dark:text-green-300">
              <CheckCircle className="h-4 w-4 flex-shrink-0" />
              <span>{successMessage}</span>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex justify-end gap-3 pt-2">
            {(block.props.showResetButton ?? true) && (
              <Button type="button" variant="outline" onClick={resetForm} disabled={isSubmitting || !isDirty}>
                {t("Reset")}
              </Button>
            )}
            <Button type="submit" disabled={isSubmitting || !isDirty}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {block.props.submitButtonText}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
